import { Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '../../constants/i18n';
import { useDiscountSummary } from '../../hooks/cart/useDiscountSummary';
import { DiscountSummaryPanel } from './DiscountSummaryPanel';
import { t } from '../../constants/i18n';
import { useCallback, useState } from 'react';

interface CheckoutPanelProps {
    totalPriceCents: number;
    originalTotalPriceCents?: number;
    cartSyncStatus: 'synced' | 'syncing' | 'degraded';
    isLoading?: boolean;
}

/**
 * 结账区组件，展示总价/折扣并提供跳转入口。
 * @param totalPriceCents 当前应付金额（分）
 * @param originalTotalPriceCents 原价（分），用于展示划线价
 * @param onCheckout 点击结账的回调
 * @param isLoading 按钮加载态
 */
export function CheckoutPanel({
    totalPriceCents,
    originalTotalPriceCents,
    cartSyncStatus,
    isLoading = false,
}: CheckoutPanelProps) {
    const hasOriginalPrice = originalTotalPriceCents && originalTotalPriceCents !== totalPriceCents;
    const [isProcessing, setIsProcessing] = useState(false);

    // 使用 Hook 获取折扣汇总数据和动作
    const discountSummary = useDiscountSummary();

    const proceedToCheckout = useCallback(() => {
        const baseUrl = '/checkout';
        // P0 Fix: Ensure discount code is securely applied or removed via URL override
        // If valid code exists -> ?discount=CODE
        // If no valid code -> ?discount= (clears any session persistence)
        const code = discountSummary.discountCode.appliedCodes.length > 0
            ? discountSummary.discountCode.appliedCodes.join(',')
            : (discountSummary.discountCode.isValid && discountSummary.discountCode.code
                ? discountSummary.discountCode.code
                : '');
        window.location.href = `${baseUrl}?discount=${code}`;
    }, [discountSummary.discountCode.code, discountSummary.discountCode.isValid]);

    const handleCheckout = async () => {
        if (isProcessing) return;
        setIsProcessing(true);
        try {
            proceedToCheckout();
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="w-full overflow-hidden space-y-4">
            {/* 折扣汇总 - 使用新组件 */}
            <DiscountSummaryPanel
                totalDiscountCents={discountSummary.totalDiscountCents}
                discounts={discountSummary.discounts}
                currency={discountSummary.currency}
                showDiscountCodeBadge={discountSummary.discountCode.isValid}
                discountCode={discountSummary.discountCode.code}
                showLineItemRemove={false}
                theme="red"
            />

            {/* 总计 */}
            <div className="flex flex-col gap-1 pt-2">
                {hasOriginalPrice && (
                    <div className="flex justify-end">
                        <span className="text-sm font-medium text-zinc-400 line-through">
                            {formatCurrency(originalTotalPriceCents!, discountSummary.currency)}
                        </span>
                    </div>
                )}

                <div className="flex justify-between items-end items-center">
                    <span className="text-lg font-bold text-zinc-900">{t('total')}</span>
                    <span className="text-2xl font-extrabold tracking-tight">
                        {formatCurrency(totalPriceCents, discountSummary.currency)}
                    </span>
                </div>
            </div>

            {/* 税费提示 */}
            <p className="text-xs text-muted-foreground text-center mt-2 mb-2!">
                {t('taxesIncluded')}
            </p>

            {cartSyncStatus === 'degraded' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <div className="flex items-center justify-between gap-3">
                        <span>
                            {t('cartSyncDegraded')}
                        </span>
                    </div>
                </div>
            )}

            {/* Checkout 按钮 */}
            <Button
                onClick={handleCheckout}
                disabled={isLoading || isProcessing}
                aria-busy={isProcessing}
                className="w-full bg-[#990000]! text-white! py-2! rounded-xl font-extrabold! text-lg! flex items-center justify-center gap-2 hover:bg-[#fbb100]! active:scale-[0.98] transition-all shadow-lg shadow-gray-200 h-14!"
                size="lg"
            >
                {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock size={20} />}
                {isProcessing ? t('checkoutLocked') : t('checkout')}
            </Button>

        </div>
    );
}
